"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Program Benefits 5009 — KUK "Your Career Roadmap".
 *
 * Port of kuk-campus-portal `components/Degree/Roadmap.tsx`: the career-path
 * cards, each an icon beside a role and what it involves, with a "talk to a
 * counsellor" CTA beside the heading.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px, and it also uses `lg:` (1024px) to switch the cards between a
 * two-column grid and a horizontal rail. This app remaps `md:` to 1230px and
 * exposes 768px as `tab:` (see globals.css `--breakpoint-*`), so the reference's
 * `md:` becomes `tab:` and its `lg:` becomes this app's `sm:` (1080px) — the
 * nearest real breakpoint to 1024px, and the one this app already uses for
 * "desktop layout starts here".
 *
 * TWO PRESENTATIONS, ONE SET OF ROWS. From `sm` up the cards are a two-column
 * grid; below it they become a horizontal scroll rail. That is v1's behaviour,
 * and both read the same `metadata` rows.
 *
 * THE CTA APPEARS TWICE in v1 — once inline beside the heading (desktop only)
 * and once full-width at the foot of the section (mobile only). Both are kept,
 * because they are the same button shown at the width each layout has room for;
 * a single CTA would either crowd the mobile heading or strand the desktop one.
 *
 * ICONS — v1 names its icons from a code-side map ('userSetting', 'userThought',
 * 'breifcaseGroup', …) and renders them as inline SVG. `DynamicIcon` accepts
 * both an icon-map key and an uploaded image URL, so the reference's names carry
 * over verbatim.
 *
 * DROPPED — v1's `custom-underline` class on the heading, a project-level rule
 * that draws a brand-coloured bar under it. This app has no equivalent global,
 * so the rule is expressed as a real CMS-coloured border instead of importing a
 * stylesheet for one selector.
 *
 * DROPPED — v1's `md:px-30` on the card grid. `30` is not in the Tailwind
 * spacing scale, so the class compiles to nothing and the grid is flush with its
 * container; the rendered behaviour is what is reproduced.
 */

/** v1: `width="40"` on the grid card icon, `"30"` on the rail card. */
const ICON_SIZE_GRID = "40";
const ICON_SIZE_RAIL = "30";

/**
 * v1's `.custom-underline` — a short accent rule under the section heading, used
 * on 12 headings across the reference. Defined in ITS globals.css as a `::after`
 * with `bottom:-6px; left:0; width:33%; height:5px; background:#9f1c33`.
 *
 * Reproduced as a real element rather than a pseudo-element so the colour can
 * come from a CMS field (a pseudo-element cannot read an inline style), and so
 * the classes are ones Tailwind certainly emits — an arbitrary `after:` variant
 * would need verifying against the built CSS.
 *
 * `w-fit` on the heading is load-bearing: the rule is 33% of the HEADING, and a
 * full-width h2 would stretch it across a third of the page.
 */
const HEADING_UNDERLINE =
  "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

interface RoadmapRow {
  id?: string | number;
  /** An `iconMap` key (e.g. "userSetting") or an uploaded icon URL. */
  meta_icon?: string;
  meta_title?: string;
  meta_sub_title?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface ProgramBenefits5009Data {
  status?: boolean | number | string;
  title?: string;
  title_start_color?: string;
  /** The rule under the heading. */
  title_end_color?: string;
  desktop_background_color?: string;
  /** Card fill + card icon tint. */
  card_hover_background_color?: string;
  gradient_color_1?: string;
  /** Card title / body. */
  subtitle_color?: string;
  description_color?: string;
  /**
   * CTA. `program_benefits` has no URL column, and the reference's button only
   * ever opens the lead form, so this is deliberately not linkable.
   */
  button_text?: string;
  button_color?: string;
  button_text_color?: string;
  button_icon_url?: string;
  metadata?: RoadmapRow[];
}

const ProgramBenefits5009 = (props: { data?: ProgramBenefits5009Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as
    | ProgramBenefits5009Data
    | undefined;
  const dispatch = useDispatch();

  const {
    title = "",
    title_start_color = "#101112",
    title_end_color = "#9F1C33",
    desktop_background_color = "#FFFFFF",
    card_hover_background_color = "#FFFFFF",
    gradient_color_1 = "#9F1C33",
    subtitle_color = "#101112",
    description_color = "#4B4B4B",
    button_text = "Talk to our counsellor",
    button_color = "#9F1C33",
    button_text_color = "#FFFFFF",
    button_icon_url = "",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  const cards = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter(
        (r) => r?.meta_status !== false && (r?.meta_title || r?.meta_sub_title),
      )
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  const handleCtaClick = useCallback(() => {
    ctaClick({
      section: "program_benefits_5009",
      component: "roadmap_counsellor",
      label: button_text?.trim() || "counsellor",
      step: "1",
      entity: "program_benefits_5009",
      parentEntity: "program_benefits_5009",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [button_text, dispatch]);

  if (!status || cards.length === 0) return null;

  const ctaStyle = {
    backgroundColor: button_color,
    color: button_text_color,
  };

  const renderCta = (className: string) => {
    if (!button_text) return null;
    const inner = (
      <>
        {button_icon_url ? (
          <DynamicIcon
            iconName={button_icon_url}
            width="18"
            height="18"
            color={button_text_color}
          />
        ) : null}
        <span>{button_text}</span>
      </>
    );
    const base = cn(
      "inline-flex h-[40px] items-center justify-center gap-2 whitespace-nowrap",
      "rounded-md px-6 text-sm font-light shadow transition-colors",
      className,
    );
    return (
      <button type="button" onClick={handleCtaClick} className={base} style={ctaStyle}>
        {inner}
      </button>
    );
  };

  const cardIcon = (row: RoadmapRow, size: string) =>
    row.meta_icon ? (
      <DynamicIcon
        iconName={row.meta_icon}
        width={size}
        height={size}
        color={gradient_color_1}
      />
    ) : null;

  return (
    <section
      className="w-full px-5 py-10 tab:p-20"
      style={{ backgroundColor: desktop_background_color }}
    >
      <div className="mx-auto w-full max-w-[1120px]">
        <div className="flex items-center justify-start gap-5">
          {title ? (
            <h2
              className="relative w-fit text-2xl font-bold tab:text-4xl"
              style={{ color: title_start_color }}
            >
              {title}
              <span
                aria-hidden
                className={HEADING_UNDERLINE}
                style={{ backgroundColor: title_end_color }}
              />
            </h2>
          ) : null}
          {/* Desktop: beside the heading, as in the reference. */}
          {renderCta("hidden sm:inline-flex")}
        </div>

        {/* ── sm and up: two-column grid ─────────────────────────────────── */}
        <div className="mt-8 hidden grid-cols-1 gap-4 sm:grid md:grid-cols-2">
          {cards.map((row, index) => (
            <div
              key={row.id ?? index}
              className="flex min-h-[146px] w-full items-start justify-start gap-5 p-10 shadow-lg"
              style={{ backgroundColor: card_hover_background_color }}
            >
              <div className="w-[10%] shrink-0">{cardIcon(row, ICON_SIZE_GRID)}</div>
              <div className="grid h-full grid-rows-[auto_1fr] gap-4">
                {row.meta_title ? (
                  <h3 className="text-xl font-semibold" style={{ color: subtitle_color }}>
                    {row.meta_title}
                  </h3>
                ) : null}
                {row.meta_sub_title ? (
                  <p className="text-sm" style={{ color: description_color }}>
                    {row.meta_sub_title}
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        {/* ── below sm: horizontal rail ──────────────────────────────────── */}
        <div className="relative sm:hidden">
          <div
            className="scrollbar-hide flex gap-4 overflow-x-auto py-10"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {cards.map((row, index) => (
              <div
                key={row.id ?? index}
                className="flex min-w-[200px] flex-col items-start gap-7 rounded-md p-5 shadow-xl"
                style={{ backgroundColor: card_hover_background_color }}
              >
                {cardIcon(row, ICON_SIZE_RAIL)}
                {row.meta_title ? (
                  <h3 className="text-base font-bold leading-5" style={{ color: subtitle_color }}>
                    {row.meta_title}
                  </h3>
                ) : null}
                {row.meta_sub_title ? (
                  <p className="text-sm font-light" style={{ color: description_color }}>
                    {row.meta_sub_title}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile: full-width at the foot, as in the reference. */}
        {renderCta("w-full sm:hidden")}
      </div>
    </section>
  );
};

export default ProgramBenefits5009;
